import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { ActionModal, Button, Card, CardEmpty, EmptyState, Icon, IconButton, Row, Rows, Spinner } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { BandAvatar } from "../shared/BandAvatar.js";
import { ApiError } from "../../app/hooks/apiError.js";
import {
  useFriends,
  useRespondFriendRequest,
  useUnfriend,
  bandInitials,
  type FriendSummary,
} from "../../app/hooks/useFriends.js";
import { AddFriendModal } from "./AddFriendModal.js";
import { BandProfileBrowser } from "./BandProfileModal.js";
import { TOP8_DRAG_TYPE, TOP8_FRIEND_DRAG_TYPE, encodeDrag } from "./top8Drag.js";
import styles from "./FriendsCard.module.scss";

/**
 * The Friends card — the artist↔artist bridge surface on `/contacts`, a
 * sibling of the crew rolodex + BandTeamCard + TeamsCard. All friends routes are `friends:manage`, so the whole
 * card is gated: a non-manage seat never mounts it (no read, no fetch). Sections: an add-friend flow (email →
 * photo-confirm → send), incoming requests (Accept / Decline), outgoing-pending, and the active-friends list
 * (each opens the browse modal; each has an unfriend action behind a destructive confirm). Designed
 * empty / loading / error states.
 */
export function FriendsCard({ onOpenBand }: { onOpenBand?: (bandId: string) => void } = {}) {
  const canManage = useCapability("friends:manage");
  // GET /friends requires friends:manage — a non-manager can't read friendships, so the card never mounts.
  if (!canManage) return null;
  return <FriendsCardInner onOpenBand={onOpenBand} />;
}

function FriendsCardInner({ onOpenBand }: { onOpenBand?: (bandId: string) => void }) {
  const list = useFriends();
  // friends:manage gates the READ (this card mounts for TMs, managers and roster views so they can browse
  // the Top 8 / friends list). friends:send is the tighter AV-only token: the request/accept/decline/unfriend
  // gate functions all require an active band_member_av seat, so the mutating affordances are offered only to
  // a caller the gate will accept (ART-162) — an affordance that is refused and a permission that is
  // unreachable are the same defect. The server still enforces independently.
  const canSend = useCapability("friends:send");
  const [adding, setAdding] = useState(false);
  // The band being browsed. The navigation stack (friend-of-a-friend tiles, Back, the stacked member
  // profile) lives inside BandProfileBrowser — one behavior for every door that opens a band profile.
  const [browsing, setBrowsing] = useState<string | null>(null);
  const [unfriendTarget, setUnfriendTarget] = useState<FriendSummary | null>(null);

  const active = list.data?.active ?? [];
  const incoming = list.data?.incoming ?? [];
  const outgoing = list.data?.outgoing ?? [];
  const isEmpty = active.length === 0 && incoming.length === 0 && outgoing.length === 0;

  // Desktop opens the browse modal in place; mobile navigates to the dedicated `/contacts/band/{id}` screen
  // (ART-195) via the callback the route wrapper supplies. No callback ⇒ modal, so the card stays
  // render-context free and its no-RouterProvider tests are unchanged.
  function openFriend(bandId: string) {
    if (onOpenBand) onOpenBand(bandId);
    else setBrowsing(bandId);
  }

  return (
    <Card
      id="friends"
      title="Friends"
      flush
      // Title-only card with a header button: float the action so the 36px button doesn't stretch the
      // header past the "Friends" line and hang dead space above the first friend row (Crew does the same since
      // its subtitle went; Team avoids it with a two-line title+subtitle block that matches the button height).
      floatActions
      /* Secondary on purpose: the left column's Add crew / Invite team are the page's primary actions.
         Hidden entirely for a non-AV browser — only an Artist-View seat can originate a request. */
      actions={
        canSend ? (
          <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
            <Icon icon={Plus} size={16} /> Add friend
          </Button>
        ) : undefined
      }
    >

      <div className={styles.body}>
        {list.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading friends…" />
          </div>
        ) : list.isError ? (
          <FriendsError error={list.error} onRetry={() => void list.refetch()} />
        ) : isEmpty ? (
          <CardEmpty>
            {canSend ? "No friends yet, search for an artist to send a request." : "No friends yet."}
          </CardEmpty>
        ) : (
          <div className={styles.sections}>
            {/* Requests (incoming to respond + outgoing pending) — one "Friend requests" header covers both,
                each row self-distinguishing (Accept/Decline vs a Pending pill). A rule separates the whole
                requests block from the friends list below, but only when both are present. */}
            {incoming.length > 0 && (
              <IncomingSection items={incoming} canSend={canSend} />
            )}
            {outgoing.length > 0 && (
              <div className={styles.section}>
                <Rows>
                  {outgoing.map((f) => (
                    <OutgoingRow key={f.friendshipId} friend={f} canSend={canSend} />
                  ))}
                </Rows>
              </div>
            )}
            {(incoming.length > 0 || outgoing.length > 0) && active.length > 0 && (
              <hr className={styles.divider} />
            )}
            {active.length > 0 && (
              // No section label here — the card is already titled "Friends", so the active list IS the
              // card's subject. The requests block above keeps its "Friend requests" header (a distinction the
              // card title doesn't carry); this one only duplicated it and cost a row of height.
              <div className={styles.section}>
                <Rows>
                  {active.map((f) => (
                    <ActiveRow
                      key={f.friendshipId}
                      friend={f}
                      canSend={canSend}
                      onOpen={() => f.friendBandId && openFriend(f.friendBandId)}
                      onUnfriend={() => setUnfriendTarget(f)}
                    />
                  ))}
                </Rows>
              </div>
            )}
          </div>
        )}
      </div>

      {adding && <AddFriendModal onClose={() => setAdding(false)} />}

      {browsing && <BandProfileBrowser initialBandId={browsing} onClose={() => setBrowsing(null)} />}

      {unfriendTarget && (
        <UnfriendConfirm friend={unfriendTarget} onDone={() => setUnfriendTarget(null)} />
      )}
    </Card>
  );
}

// ---- incoming requests ----------------------------------------------------------------------------------

function IncomingSection({ items, canSend }: { items: FriendSummary[]; canSend: boolean }) {
  const respond = useRespondFriendRequest();

  return (
    <div className={styles.section}>
      <p className={styles.sectionLabel}>Friend requests</p>
      <Rows>
        {items.map((f) => {
          const pendingThis = respond.isPending && respond.variables?.friendshipId === f.friendshipId;
          const accepting = pendingThis && respond.variables?.action === "accept";
          const declining = pendingThis && respond.variables?.action === "decline";
          return (
            <Row
              key={f.friendshipId}
              avatar={<BandAvatar bandId={f.friendBandId} avatarMediaId={f.avatarMediaId} initials={bandInitials(f.bandName)} />}
              name={f.bandName || "Unnamed band"}
              // Accept/Decline route through the same AV-only gate as sending, so a non-AV browser sees the
              // pending request read-only rather than two buttons that would 403 (ART-162).
              trailing={
                !canSend ? undefined : (
                <div className={styles.rowActions}>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={declining}
                  disabled={pendingThis}
                  onClick={() =>
                    f.friendshipId && respond.mutate({ friendshipId: f.friendshipId, action: "decline" })
                  }
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  loading={accepting}
                  disabled={pendingThis}
                  onClick={() =>
                    f.friendshipId && respond.mutate({ friendshipId: f.friendshipId, action: "accept" })
                  }
                >
                  Accept
                </Button>
                </div>
                )
              }
            />
          );
        })}
      </Rows>
    </div>
  );
}

// ---- outgoing (sent, pending) request row ---------------------------------------------------------------

function OutgoingRow({ friend, canSend }: { friend: FriendSummary; canSend: boolean }) {
  // Revoking a sent request is the SAME DELETE as unfriending: the gate's remove_friend has no status guard,
  // so it withdraws a still-pending row too (the recipient's Incoming entry clears on the next read). No
  // destructive confirm — nothing was shared yet, and the request can simply be re-sent. AV-only (the gate
  // refuses a non-AV seat), so the button is offered only where canSend, the unfriend affordance's rule.
  const revoke = useUnfriend();
  const name = friend.bandName || "Unnamed band";
  return (
    <Row
      avatar={<BandAvatar bandId={friend.friendBandId} avatarMediaId={friend.avatarMediaId} initials={bandInitials(friend.bandName)} />}
      name={name}
      trailing={
        <div className={styles.rowActions}>
          <span className={styles.pillMuted}>Pending</span>
          {canSend && friend.friendBandId && (
            <IconButton
              icon={Trash2}
              label={`Revoke request to ${name}`}
              tone="danger"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => friend.friendBandId && revoke.mutate(friend.friendBandId)}
            />
          )}
        </div>
      }
    />
  );
}

// ---- active friend row ----------------------------------------------------------------------------------

function ActiveRow({
  friend,
  canSend,
  onOpen,
  onUnfriend,
}: {
  friend: FriendSummary;
  canSend: boolean;
  onOpen: () => void;
  onUnfriend: () => void;
}) {
  const name = friend.bandName || "Unnamed band";
  return (
    <Row
      // A friend row is a drag source for the Top 8 grid (the spec's `friend:` payload). It carries the
      // whole band so the tile can paint without a second lookup.
      draggable={!!friend.friendBandId}
      onDragStart={(e) => {
        if (!friend.friendBandId) return;
        e.dataTransfer.setData(
          TOP8_DRAG_TYPE,
          encodeDrag({
            kind: "friend",
            band: {
              friendBandId: friend.friendBandId,
              bandName: friend.bandName,
              slug: friend.slug,
              avatarMediaId: friend.avatarMediaId,
            },
          }),
        );
        // The friend marker lets the grid answer with dropEffect "copy" — a "move" answer against
        // effectAllowed "copy" makes Chromium cancel the drop outright (see top8Drag.ts).
        e.dataTransfer.setData(TOP8_FRIEND_DRAG_TYPE, "1");
        e.dataTransfer.effectAllowed = "copy";
      }}
      avatar={<BandAvatar bandId={friend.friendBandId} avatarMediaId={friend.avatarMediaId} initials={bandInitials(friend.bandName)} />}
      name={name}
      onOpen={onOpen}
      // Unfriend is the same AV-only gate; a non-AV browser gets a read-only, still-openable row.
      trailing={
        canSend ? (
          <IconButton icon={Trash2} label={`Unfriend ${name}`} tone="danger" size="sm" onClick={onUnfriend} />
        ) : undefined
      }
    />
  );
}

// ---- unfriend confirm -----------------------------------------------------------------------------------

function UnfriendConfirm({ friend, onDone }: { friend: FriendSummary; onDone: () => void }) {
  const unfriend = useUnfriend();
  const [error, setError] = useState<string | null>(null);
  const name = friend.bandName || "this band";

  function onConfirm() {
    if (!friend.friendBandId) return;
    setError(null);
    unfriend.mutate(friend.friendBandId, {
      onSuccess: onDone,
      onError: (err) => setError(err.message),
    });
  }

  return (
    <ActionModal
      title={`Unfriend ${name}`}
      confirmLabel="Unfriend"
      loading={unfriend.isPending}
      onConfirm={onConfirm}
      onCancel={onDone}
    >
      <p className={styles.confirmText}>Are you sure you want to unfriend {name}?</p>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </ActionModal>
  );
}

// ---- error state ----------------------------------------------------------------------------------------

function FriendsError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) {
    return <EmptyState title="Friends unavailable" description="Your role on this band doesn't include Friends." />;
  }
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return (
    <EmptyState title="Couldn't load friends" description={message} action={<Button onClick={onRetry}>Retry</Button>} />
  );
}
